import { AdvancedProperty } from '@/models/Property'
import DataService from '@/services/DataService'
import { useProductsViewStore } from '@/store/productsViewStore'
import { browserStorage } from '@/utils/browserStorage'
import { toComparable } from '@/utils/tools'
import { computed, onMounted, ref } from 'vue'

export function useTableColumnResize() {
  // -----------
  // -- Setup --
  // -----------
  const productsViewStore = useProductsViewStore()

  let stagedColumnSize: number | undefined
  let columnResizeData: { startX: number; comparableProperty: string; cellWidth: number } | undefined

  const columnSizes = ref<{ [comparableProperty: string]: string }>({})
  const minWidth = 20

  const columnSizesUpdated = computed(() => {
    columnSizes.value
    return new Date()
  })

  // ---------------
  // -- Lifecycle --
  // ---------------
  onMounted(async () => {
    const remoteSettings = await DataService.getSavedUserTableColumnSizes()

    if (remoteSettings) {
      columnSizes.value = remoteSettings || {}
    } else {
      const localSettings = browserStorage.get('productTable-columnSizes')
      columnSizes.value = localSettings || {}

      if (localSettings) {
        // browserStorage.delete('productTable-columnSizes')
        saveSettings()
      }
    }
  })

  // ---------------
  // -- Functions --
  // ---------------
  const saveSettings = async () => {
    try {
      // browserStorage.set('productTable-columnSizes', columnSizes.value)
      await DataService.saveUserTableColumnSizes(columnSizes.value)
    } catch (err) {
      console.log('error writing settings for table column sizes')
    }
  }

  const startColumnResize = (startEvent: MouseEvent, property: AdvancedProperty, resizeLine?: HTMLElement, cb?: () => void) => {
    const anchor = startEvent.target as HTMLElement
    const anchorRect = anchor.getBoundingClientRect()

    const mouseDistanceToAnchor = anchorRect.right - startEvent.x

    const handleMouseMove = (event: MouseEvent) => {
      if (columnResizeData) {
        const width = columnResizeData.cellWidth + (event.x - columnResizeData.startX)
        stagedColumnSize = Math.max(minWidth, width + mouseDistanceToAnchor)

        if (resizeLine) {
          const tableOffsetX = productsViewStore.fullscreen ? 0 : productsViewStore.productSideMenuWidth
          const scrollLeft = document.documentElement.scrollLeft

          let minWidthOverlap = 0
          if (width < minWidth) {
            minWidthOverlap = minWidth - width
          }

          resizeLine.style.left = `${event.x - tableOffsetX + scrollLeft + minWidthOverlap + mouseDistanceToAnchor}px`
        }
      }
    }

    const preventDefault = (e: Event) => {
      e.preventDefault()
    }

    columnResizeData = {
      startX: anchorRect.right,
      comparableProperty: toComparable(property.defaultHeader),
      cellWidth: anchor.parentElement?.getBoundingClientRect().width || 0,
    }

    resizeLine?.classList.remove('d-none')
    handleMouseMove(startEvent)
    document.body.classList.add('cursor-col-resize')

    document.addEventListener('wheel', preventDefault, { passive: false })
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener(
      'mouseup',
      () => {
        if (columnResizeData) {
          document.removeEventListener('wheel', preventDefault)
          document.removeEventListener('mousemove', handleMouseMove)
          resizeLine?.classList.add('d-none')
          document.body.classList.remove('cursor-col-resize')
          columnSizes.value[columnResizeData.comparableProperty] = `${stagedColumnSize}px`
          stagedColumnSize = undefined
          saveSettings()
          cb?.()
        }
      },
      { once: true },
    )
  }

  const resetColumnSize = (property: AdvancedProperty) => {
    delete columnSizes.value[toComparable(property.defaultHeader)]
  }

  const getColumnSize = (property: AdvancedProperty) => columnSizes.value[toComparable(property.defaultHeader)] || '250px'

  return { columnSizesUpdated, getColumnSize, startColumnResize, resetColumnSize }
}
